import { useCallback } from 'react'
import { Button, GenericDialog } from '@/common'
import useI18n from '@/shared/hooks/usei18n'
import { ImportedGood, useBulkDeleteImportedGoods } from '@/features/cbam-reporting/hooks/importedGoods'
import toast from 'react-hot-toast'
import { Trans } from 'react-i18next'

export default function BulkDeleteImportedGoodsDialog({
  isOpen,
  close,
  selectedGoods,
  organizationId,
  cbamReportId,
  onSuccess
}: {
  isOpen: boolean
  close: () => void
  selectedGoods: ImportedGood[]
  organizationId: string
  cbamReportId: string
  onSuccess: () => void
}) {
  const { t } = useI18n()
  const { mutateAsync: bulkDelete, isPending } = useBulkDeleteImportedGoods(organizationId, cbamReportId)

  const handleDelete = useCallback(async () => {
    try {
      await bulkDelete({
        ids: selectedGoods.map(good => good.id)
      })

      toast.success(
        t('cbamReporting:bulkDelete.notifications.success', { count: selectedGoods.length })
      )
      onSuccess()
      close()
    } catch (error) {
      console.error('Bulk delete failed:', error)
      toast.error(t('cbamReporting:bulkDelete.notifications.error'))
    }
  }, [bulkDelete, selectedGoods, t, onSuccess, close])

  return (
    <GenericDialog
      isOpen={isOpen}
      close={close}
      title={t('cbamReporting:bulkDelete.title')}
      size="md"
    >
      <div className="space-y-4">
        <p>
          <Trans
            i18nKey="cbamReporting:bulkDelete.description"
            values={{ count: selectedGoods.length }}
            components={{ strong: <strong /> }}
          />
        </p>
        <footer className="gap-2 flex justify-end">
          <Button variant="neutral" onClick={close}>
            {t('common:actions.cancel')}
          </Button>
          <Button variant="danger" onClick={handleDelete} isLoading={isPending}>
            {t('cbamReporting:bulkDelete.actions.delete', { count: selectedGoods.length })}
          </Button>
        </footer>
      </div>
    </GenericDialog>
  )
}
